
import React, { createContext, useState, useEffect, useContext } from "react";
import { getRecommendationSections } from "./fetches/recommendations";
import CheckFitmentForSkuBases from "Utilities/checkFitment/checkFitment"
import { UserContext } from "Contexts/UserContext/UserContext";
import { VehicleContext } from "Contexts/VehicleContext";
import AppName from "AppName";
import useSegment from "Hooks/useSegment";
import Retry from 'Utilities/Retry'
import useRecentHistory from 'Hooks/useRecentHistory';
import { getProductRecommendationData } from './fetches/productRecommendationData';
import {getSuperMarketAffinity} from 'Utilities/Instrumentation/SegmentUtilities/segmentFunctions'

const RecommendationContext = createContext();

const RecommendationProvider = ({
  children,
  value,
  sections,
  skuBaseNumbers,
  skuVariantNumbers,
  articleIds,
  isPageLoading,
  keywords,
}) => {
  const { context: userContext } = useContext(UserContext);
  const { context: vehicleContext } = useContext(VehicleContext);
  const { sendSegmentTrackEventWithName } = useSegment();
  const [recommendationData, setRecommendationData] = useState(value || {});
  const [isLoading, setIsLoading] = useState(true);
  const [inView, setInView] = useState(false);
  // an object of the form "PDP_Bottom": true (true means the section is in view)
  // we're trying to avoid sending a section twice
  const [recommendationSectionsInView, setRecommendationSectionsInView] =
    useState({});
  // an object of the form "PDP_Bottom": true (true means we've sent impressions for that section)
  // we're trying to avoid sending a section twice
  const [recommendationImpressionsSent, setRecommendationImpressionsSent] = useState({});  
  const [hasHydratedClientProductData, setHasHydratedClientProductData] = useState(false)

  useEffect(() => {
    try {
      const isReady = userContext?.hydrated && vehicleContext?.hydrated && vehicleContext?.selectedVehicle?.hydrated;
      if (isReady && inView && !userContext.isBot()) {
        const userVin = userContext.userVin();
        const webUserGuid = userContext.getGuid();
        const selectedVehicleBases = vehicleContext?.customerVehicles
          ? vehicleContext.customerVehicles.filter(
            (vehicle) =>
              vehicle &&
              vehicleContext.selectedVehicle.vehicleBaseId &&
              vehicle.vehicleBaseId === vehicleContext.selectedVehicle.vehicleBaseId
          )
          : [];
        const selectedRaceTypes = vehicleContext?.customerVehicles
          ? vehicleContext.customerVehicles.filter(
            (vehicle) =>
              vehicle &&
              vehicleContext.selectedVehicle.raceTypeId &&
              vehicle.raceTypeId === vehicleContext.selectedVehicle.raceTypeId
          )
          : [];
        const selectedVehicle = []
          .concat(selectedVehicleBases || [])
          .concat(selectedRaceTypes || [])[0];
        const ymmData = selectedVehicle
          ? {
            year: selectedVehicle.year,
            make: selectedVehicle.makeId,
            model: selectedVehicle.modelId,
            raceTypeId: selectedVehicle.raceTypeId,
          }
          : null;

        if (!isPageLoading) {
          getRecommendationSections(
            sections,
            skuBaseNumbers,
            skuVariantNumbers,
            articleIds,
            userVin,
            webUserGuid,
            ymmData,
            keywords
          )
            .then(setRecommendationData)
            .then(() => setIsLoading(false));
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, [
    inView,
    isPageLoading,
    userContext && userContext.hydrated,
    vehicleContext?.hydrated,
    vehicleContext?.selectedVehicle?.hydrated
  ]);

  useEffect(() => {
    if (!isLoading && !hasHydratedClientProductData) {
      setClientRecommendations(recommendationData);
    }
  }, [isLoading])

  async function setClientRecommendations(recommendationsResponse) {
    try {
      if (recommendationsResponse.recommendationSections && recommendationsResponse.recommendationSections.length) {
        for (let i = 0; i < recommendationsResponse.recommendationSections.length; i++) {
          for (let j = 0; j < recommendationsResponse.recommendationSections[i].recommendationGroups.length; j++) {
            if (recommendationsResponse.recommendationSections[i].recommendationGroups[j].clientDataType == "PdpRecentHistory") {
              let history = useRecentHistory.GetRecentHistory("Pdp", "skuBaseNumber");
              recommendationsResponse.recommendationSections[i].recommendationGroups[j].recommendations = history.map(x => {
                return {
                  __typename: "Product",
                  skuBase: x.skuBaseNumber,
                  publicSkuBaseDetails: {
                    displayTitle: x.title,
                    productPageUrl: x.url,
                    thumbnailImageLink: x.imageUrl,
                    skuBaseNumber: x.skuBaseNumber,
                    brand: x.brand,
                    segmentCategorization: x.segmentCategorization
                  }
                }
              });
            }
          }
        }
        const hydrated = await getProductRecommendationData(recommendationData, userContext.getGuid())
        setRecommendationData(hydrated);
      }
      setHasHydratedClientProductData(true);
      return recommendationsResponse;
    }
    catch (err) {
      setHasHydratedClientProductData(true);
      console.error(err);
      return recommendationsResponse;
    }
  };

  useEffect(() => {
    if (userContext.zipCode && recommendationData.recommendationSections?.length && !recommendationData.shippingHydrated) {
      addShippingEstimatesIfApplicable();
    }
  }, [userContext.zipCode, recommendationData])

  useEffect(() => {
    if ((vehicleContext?.selectedVehicle?.vehicleBaseId || vehicleContext?.selectedVehicle?.raceTypeId) && recommendationData.recommendationSections?.length && !recommendationData.fitmentHydrated) {
      checkFitmentForSections()
    }
  }, [vehicleContext?.selectedVehicle?.vehicleBaseId, vehicleContext?.selectedVehicle?.raceTypeId, recommendationData])

  const addShippingEstimatesIfApplicable = async () => {
    try {
      setRecommendationData(x => { return { ...x, shippingHydrated: true } });
      let skusToGet = []
      let skuModels = []
      for (let i = 0, ilen = recommendationData.recommendationSections.length; i < ilen; i++) {
        for (let j = 0, jlen = recommendationData.recommendationSections[i].recommendationGroups.length; j < jlen; j++) {
          if (recommendationData.recommendationSections[i].recommendationGroups[j].getShippingEstimate) {
            for (let k = 0, klen = recommendationData.recommendationSections[i].recommendationGroups[j].recommendations.length; k < klen; k++) {
              let rec = recommendationData.recommendationSections[i].recommendationGroups[j].recommendations[k]
              if (rec.skuBase || rec.skuBaseNumber) {
                skusToGet.push(rec.skuBase ?? rec.skuBaseNumber)
                skuModels.push(rec);
              }
            }
          }
        }
      }
      if (!skusToGet.length) {
        return;
      }
      if (!userContext.zipCode)
        return
      //checks if the zip string contains non-numeric characters
      if (!(/^\d+$/.test(userContext.zipCode))) {
        return
      }
      if (userContext.zipCode.length != 5) {
        return
      }
      const module = await Retry(() => import('Utilities/shippingEstimates/skuBaseShippingEstimates'))
      const estimates = await module.default(skusToGet, userContext.zipCode)
      for (let i = 0, ilen = skuModels.length; i < ilen; i++) {
        const thisSkuBase = skuModels[i].skuBase || skuModels[i].skuBaseNumber;
        const match = estimates.find(x => x.skuBase === thisSkuBase);
        if (match) {
          skuModels[i].shippingEstimate = match;
        } else {
          skuModels[i].shippingEstimate = "none";
        }
      }
      setRecommendationData(x => { return { ...x, ...recommendationData } });
    } catch (err) {
      console.log("error getting shipping estimates for recommendations", err)
    }
  }

  useEffect(() => {
    // there's a timing issue between sections being in view and all the data being loaded
    // this use effect will ensure that recommendation impressions are sent
    // for in view sections (ONLY ONCE) and that it happens after the sections are loaded
    if (!isLoading && hasHydratedClientProductData) {
      // this means that the recs are loaded (we start with isLoading true by default)
      const inViewSections = Object.entries(recommendationSectionsInView)
        .filter(([_, sectionInView]) => sectionInView)
        .map(([section]) => section);
      inViewSections.forEach((section) =>
        sendSegmentImpressionsForSection(section)
      );
    }
  }, [isLoading, recommendationSectionsInView, hasHydratedClientProductData]);

  const sendSegmentImpressionsForSection = (section) => {
    // don't send the same impressions twice
    if (recommendationImpressionsSent?.[section]) {
      return;
    }

    const recs = getRecommendationSection(section);
    const allRecommendations =
      recs?.recommendationGroups?.flatMap((rec) => rec?.recommendations) ?? [];
    if (!allRecommendations || !allRecommendations.length) {
      return;
    }

    // todo
    // do we want to send impressions for articles?
    // what about landing pages?
    // not yet... at least for parity
    sendSegmentTrackEventWithName("Product List Viewed", {
      user_vin: `${userContext.userVin()}`,
      web_user_guid: `${userContext.getGuid()}`,
      list_id: "Recommendations " + section,
      category: AppName,
      products: allRecommendations
        .filter((rec) => rec.publicSkuBaseDetails != null || rec.publicSkuBaseDetails != undefined || rec.comparisonValues != null)
        .map((rec, i) => {
          let price = rec.publicSkuBaseDetails?.salePrice ||
            parseFloat(rec.pricing?.priceString?.replace('$', '')) ||
            rec.publicSkuBaseDetails?.pricing?.salePrice || 
            parseFloat(rec.publicSkuBaseDetails?.pricing?.salePriceString?.replace('$', ''))

          if (!price) {
            console.error('price is null for segment event')
            console.error(JSON.stringify(rec))
          }

          let availability = rec.publicSkuBaseDetails?.availabilityEnum ?? rec.availabilityEnum
          if(!availability){
            availability = rec.publicSkuBaseDetails?.availability ?? rec.availability
          }

          let reviews = rec.publicSkuBaseDetails?.reviewCount ?? rec.reviewCount

          return {
            product_id: rec.publicSkuBaseDetails?.skuBaseNumber ?? rec.skuBaseNumber,
            sku: rec.publicSkuBaseDetails?.skuBaseNumber ?? rec.skuBaseNumber,
            name: rec.publicSkuBaseDetails?.displayTitle ?? rec.displayTitle,
            price: price,
            category: rec.publicSkuBaseDetails?.segmentCategorization ?? rec.segmentCategorization,
            url: rec.publicSkuBaseDetails?.productPageUrl ?? rec.productPageUrl,
            image_url: rec.publicSkuBaseDetails?.thumbnailImageLink ?? rec.imageLink,
            quantity: 1,
            brand: rec.publicSkuBaseDetails?.brand ?? rec.brand,
            position: i,
            availability: availability,
            rating: reviews ? (rec.publicSkuBaseDetails?.rating ?? rec.rating) : 0,
            reviews: reviews,
            audience_affinity: getSuperMarketAffinity(rec.publicSkuBaseDetails ?? rec)
          };
        }),
    });
    setRecommendationImpressionsSent((impressionState) => ({
      ...impressionState,
      [section]: true,
    }));
  };

  const setRecommendationSectionInView = (section) => {
    try {
      setInView(true);
      setRecommendationSectionsInView((inViewState) => ({
        ...inViewState,
        [section]: true,
      }));
    } catch (e) {
      console.error(e);
    }
  };

  const getRecommendationSection = (sectionType) => {
    return recommendationData.recommendationSections?.find(
      (section) => section.sectionType == sectionType
    );
  };

  const checkFitmentForSections = async () => {
    try {
      setRecommendationData(x => { return { ...x, fitmentHydrated: true } });

      let skusToGet = []
      let skuModels = []

      for (let i = 0, ilen = recommendationData.recommendationSections.length; i < ilen; i++) {
        for (let j = 0, jlen = recommendationData.recommendationSections[i].recommendationGroups.length; j < jlen; j++) {
          if (recommendationData.recommendationSections[i].recommendationGroups[j].checkFitment) {
            for (let k = 0, klen = recommendationData.recommendationSections[i].recommendationGroups[j].recommendations.length; k < klen; k++) {
              let rec = recommendationData.recommendationSections[i].recommendationGroups[j].recommendations[k]
              if ((rec.skuBase || rec.skuBaseNumber)) {
                skusToGet.push(rec.skuBase ?? rec.skuBaseNumber)
                skuModels.push(rec)
              }
            }
          }
        }
      }

      if (skuModels.every(x => x.excludeFromThisPartFitsMyVehicle)) {
        return
      }

      let response = await CheckFitmentForSkuBases(skusToGet, vehicleContext?.selectedVehicle?.vehicleBaseId, vehicleContext?.selectedVehicle?.raceTypeId)
      let vehicleBaseSelected = vehicleContext?.selectedVehicle?.vehicleBaseId != undefined || vehicleContext?.selectedVehicle?.vehicleBaseId != null

      for (let i = 0; i < skuModels.length; i++) {
        const thisSkuBase = skuModels[i].skuBase || skuModels[i].skuBaseNumber
        let match = response?.find(x => x.key === thisSkuBase)

        if (match) {
          if (vehicleBaseSelected) {
            skuModels[i].fitment = match.value?.vehicleBaseFitment?.[0]?.value ?? "none"
          }
          else {
            skuModels[i].fitment = match.value?.raceTypeFitment?.[0]?.value ?? "none"
          }
        }
        else {
          skuModels[i].fitment = "none"
        }
      }
      setRecommendationData(x => { return { ...x, ...recommendationData } });
    } catch (err) {
      console.error("error checking fitment for recommendations", err);
    }
  }

  // todo error boundary
  return (
    <RecommendationContext.Provider
      value={{
        getRecommendationSection,
        isLoading,
        hasHydratedClientProductData,
        recommendationData,
        setRecommendationSectionInView,
      }}
    >
      {children}
    </RecommendationContext.Provider>
  );
};

export { RecommendationProvider, RecommendationContext };